import React from 'react';
import {
  Home,
  Users,
  Briefcase,
  Award,
  UserCheck,
  Clipboard,
  Gift,
  BarChart2,
  FileText,
  Search,
  TrendingUp,
  Calendar,
  Download,
  Filter,
  Layers,
  BookOpen,
  Target,
  Clock,
  PlayCircle,
  ShoppingCart,
  CreditCard,
  UserPlus,
  Edit,
  User,
  Lock,
  Tool,
  Tag,
} from 'react-feather';

/**
 * Get menu configuration for a specific role
 */
export const getMenuConfigForRole = (role, userType) => {
  // Normalize role to string to handle both number and string inputs
  const roleStr = String(role);

  switch (roleStr) {
    case 'admin':
      return getAdminMenuConfig();
    case 'state':
    case '4':
      return getStateMenuConfig();
    case '1':
      return getSchoolMenuConfig(userType);
    case '2':
      return getSchoolTeacherMenuConfig();
    case '3':
      return getTeacherMenuConfig();
    default:
      console.warn('Unknown role for menu config:', role);
      return [];
  }
};

/**
 * Admin Menu Configuration
 */
const getAdminMenuConfig = () => [
  {
    label: 'Home',
    to: '/admin/admin-dashboard',
    activePaths: ['/admin/admin-dashboard'],
  },
  {
    // Sysadmin-only: this whole menu config is returned only for role 'admin', and the
    // /dev-tools route is also gated to admin — so "Test" (JWT token generator etc.) is
    // visible to sysadmins only.
    label: 'Test',
    to: '/dev-tools',
    activePaths: ['/dev-tools'],
  },
  {
    label: 'User Management',
    megaMenu: {
      sections: [
        {
          title: 'Teachers',
          items: [
            {
              label: 'All Teachers',
              description: 'View all teachers',
              icon: <Users size={18} />,
              to: '/admin/teachers-lists',
            },
          ],
        },
        {
          title: 'Schools',
          items: [
            {
              label: 'All Schools',
              description: 'View all schools',
              icon: <Briefcase size={18} />,
              to: '/admin/school-lists',
            },
            {
              // Sysadmin only, like the rest of this config. Granting the role is
              // the power to see several schools at once, so it stays with us
              // rather than being delegated to the organizations themselves.
              label: 'Organization Admins',
              description: 'One login that administers several schools',
              icon: <Users size={18} />,
              to: '/admin/org-admins',
            },
          ],
        },
        {
          title: 'Support',
          items: [
            {
              // Sysadmin only, like the rest of this config. Read-only: the
              // session can see any screen and change nothing.
              label: 'Impersonate User',
              description: "See a customer's screens as they see them",
              icon: <Users size={18} />,
              to: '/admin/impersonate-user',
            },
          ],
        },
        {
          title: 'Others',
          items: [
            {
              label: 'Promo Codes',
              description: 'Create a discount code',
              icon: <Tag size={18} />,
              to: '/admin/promo-codes',
            },
            {
              label: 'Influencers',
              description: 'View influencers',
              icon: <UserCheck size={18} />,
              to: '/admin/influences',
            },
          ],
        },
      ],
    },
  },
  {
    label: 'Content',
    megaMenu: {
      sections: [
        {
          title: 'Courses',
          items: [
            {
              label: 'Core Classes',
              description: 'View course content',
              icon: <BookOpen size={18} />,
              to: '/main-dashboard',
            },
            {
              label: 'Custom Training',
              description: 'Manage custom PDF training',
              icon: <FileText size={18} />,
              to: '/admin/custom-training',
            },
          ],
        },
        {
          title: 'Tools',
          items: [
            {
              label: 'Quiz Verification',
              description: 'Verify quiz content',
              icon: <Clipboard size={18} />,
              to: '/admin/quiz-backdoor',
            },
          ],
        },
      ],
    },
  },
  {
    label: 'Prospects',
    megaMenu: {
      sections: [
        {
          title: 'Applications',
          items: [
            {
              label: 'Grant Applications',
              description: 'View grant applications',
              icon: <Clipboard size={18} />,
              to: '/admin/grant-applications',
            },
            {
              label: 'Monthly Drawing',
              description: 'Drawing subscribers',
              icon: <Gift size={18} />,
              to: '/admin/drawing-entries',
            },
          ],
        },
      ],
    },
  },
  {
    label: 'Analytics',
    megaMenu: {
      sections: [
        {
          title: 'Reports',
          items: [
            {
              label: 'Visitor Analytics',
              description: 'View visitor data',
              icon: <BarChart2 size={18} />,
              badge: 'TBD',
              // to: '/admin/visitors', // Disabled until feature is ready
            },
            {
              label: 'Export Data',
              description: 'Download reports',
              icon: <Download size={18} />,
              badge: 'TBD',
              // to: '/admin/export', // Disabled until feature is ready
            },
          ],
        },
      ],
    },
  },
  {
    label: 'Account',
    megaMenu: {
      sections: [
        {
          title: 'Profile',
          items: [
            {
              label: 'Update Profile',
              description: 'Edit your profile information',
              icon: <User size={18} />,
              to: '/profile',
            },
            {
              label: 'Change Password',
              description: 'Update your password',
              icon: <Lock size={18} />,
              to: '/change-password',
            },
          ],
        },
        {
          title: 'Developer',
          items: [
            {
              label: 'Dev Tools',
              description: 'JWT tokens & API docs',
              icon: <Tool size={18} />,
              to: '/dev-tools',
            },
          ],
        },
      ],
    },
  },
];

/**
 * State Menu Configuration
 */
const getStateMenuConfig = () => [
  {
    label: 'Home',
    megaMenu: {
      sections: [
        {
          title: 'Overview',
          items: [
            {
              label: 'State Overview',
              description: 'View all metrics',
              icon: <BarChart2 size={18} />,
              to: '/admin/state-dashboard',
            },
            {
              label: 'All Schools',
              description: 'Browse schools',
              icon: <Layers size={18} />,
              to: '/state/schools',
            },
          ],
        },
        {
          title: 'Quick Actions',
          items: [
            {
              label: 'View Stats',
              description: 'Quick metrics',
              icon: <BarChart2 size={18} />,
              to: '/state/stats',
            },
            {
              label: 'Export Data',
              description: 'Download CSV',
              icon: <Download size={18} />,
              to: '/state/export',
            },
            {
              label: 'Search Schools',
              description: 'Find specific',
              icon: <Search size={18} />,
              to: '/state/search',
            },
          ],
        },
        {
          title: 'Recent',
          items: [
            {
              label: 'Latest Updates',
              description: 'Recent activity',
              icon: <Calendar size={18} />,
              to: '/state/updates',
            },
            {
              label: 'This Week',
              description: 'Weekly summary',
              icon: <Calendar size={18} />,
              to: '/state/weekly',
            },
          ],
        },
      ],
    },
  },
  {
    label: 'Schools',
    megaMenu: {
      sections: [
        {
          title: 'Browse',
          items: [
            {
              label: 'All Schools',
              description: 'View all schools',
              icon: <Layers size={18} />,
              to: '/state/schools',
            },
            {
              label: 'Search Schools',
              description: 'Find specific',
              icon: <Search size={18} />,
              to: '/state/schools/search',
            },
            {
              label: 'School Details',
              description: 'View details',
              icon: <FileText size={18} />,
              to: '/state/schools/details',
            },
          ],
        },
        {
          title: 'Filter By',
          items: [
            {
              label: 'By District',
              description: 'Filter by district',
              icon: <Filter size={18} />,
              to: '/state/schools?filter=district',
            },
            {
              label: 'By City',
              description: 'Filter by city',
              icon: <Filter size={18} />,
              to: '/state/schools?filter=city',
            },
            {
              label: 'By Status',
              description: 'Training status',
              icon: <Filter size={18} />,
              to: '/state/schools?filter=status',
            },
          ],
        },
        {
          title: 'Actions',
          items: [
            {
              label: 'Compare Schools',
              description: 'Compare data',
              icon: <BarChart2 size={18} />,
              to: '/state/schools/compare',
            },
            {
              label: 'Export List',
              description: 'Download list',
              icon: <Download size={18} />,
              to: '/state/schools/export',
            },
          ],
        },
      ],
    },
  },
  {
    label: 'Analytics',
    megaMenu: {
      sections: [
        {
          title: 'Reports',
          items: [
            {
              label: 'State Reports',
              description: 'View reports',
              icon: <FileText size={18} />,
              to: '/state/analytics/reports',
            },
            {
              label: 'School Comparisons',
              description: 'Compare schools',
              icon: <BarChart2 size={18} />,
              to: '/state/analytics/compare',
            },
            {
              label: 'Export',
              description: 'Download data',
              icon: <Download size={18} />,
              to: '/state/analytics/export',
            },
          ],
        },
      ],
    },
  },
  {
    label: 'Reports',
    megaMenu: {
      sections: [
        {
          title: 'Certifications',
          items: [
            {
              label: 'Certifications Overview',
              description: 'View all',
              icon: <Award size={18} />,
              to: '/state/reports/certifications',
            },
          ],
        },
        {
          title: 'Custom',
          items: [
            {
              label: 'Custom Reports',
              description: 'Create report',
              icon: <FileText size={18} />,
              to: '/state/reports/custom',
            },
            {
              label: 'Date Range Reports',
              description: 'By date range',
              icon: <Calendar size={18} />,
              to: '/state/reports/date-range',
            },
          ],
        },
      ],
    },
  },
  {
    label: 'Account',
    megaMenu: {
      sections: [
        {
          title: 'Profile',
          items: [
            {
              label: 'Update Profile',
              description: 'Edit your profile information',
              icon: <User size={18} />,
              to: '/profile',
            },
            {
              label: 'Change Password',
              description: 'Update your password',
              icon: <Lock size={18} />,
              to: '/change-password',
            },
          ],
        },
      ],
    },
  },
];

/**
 * School Menu Configuration with Mega Menu
 */
const getSchoolMenuConfig = (userType) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user._id || user.id;
  const isIndividual = userType === 'Individual';

  return [
    {
      label: 'Home',

      megaMenu: {
        sections: [
          {
            title: 'Overview',
            items: [
              {
                label: 'My Dashboard',
                description: 'View training progress',
                icon: <Home size={18} />,
                to: '/main-dashboard',
              },
              {
                label: 'My Certifications',
                description: 'Track your certifications',
                icon: <Award size={18} />,
                to: '/my-certifications',
              },
              {
                label: 'Emergency Protocol',
                description: 'View emergency procedures',
                icon: <FileText size={18} />,
                to: `/emergencycertificate/${userId}`,
              },
            ],
          },
        ],
      },
    },
    ...(isIndividual ? [] : [
      {
        label: 'Teacher Management',
        megaMenu: {
          sections: [
            {
              title: 'Teachers',
              items: [
                {
                  label: 'My Teachers',
                  description: 'View all teachers',
                  icon: <Users size={18} />,
                  to: '/my-teachers',
                },
                {
                  label: 'Teacher Certifications Dashboard',
                  description: 'Manage teacher certifications and assign training',
                  icon: <Award size={18} />,
                  to: '/teacher-certifications-dashboard',
                },

                {
                  label: 'Add Teacher',
                  description: 'Add new teacher',
                  icon: <UserPlus size={18} />,
                  to: '/add-school-teacher',
                },
              ],
            },
          ],
        },
      },
    ]),
    ...(isIndividual ? [] : [
      {
        label: 'Administrator Management',
        to: '/administrator-management',
        activePaths: ['/administrator-management'],
      },
    ]),
    {
      label: 'Account',
      megaMenu: {
        sections: [
          {
            title: 'Profile',
            items: [
              {
                label: 'Update Profile',
                description: 'Edit your profile information',
                icon: <User size={18} />,
                to: '/profile',
              },
              {
                label: 'Change Password',
                description: 'Update your password',
                icon: <Lock size={18} />,
                to: '/change-password',
              },
            ],
          },
          {
            title: 'Certifications',
            items: [
              {
                label: 'View School Certificates',
                description: 'View earned certificates',
                icon: <Award size={18} />,
                to: '/certificates',
              },
            ],
          },
          {
            title: 'Billing',
            items: [
              {
                label: 'Order History',
                description: 'View past orders',
                icon: <ShoppingCart size={18} />,
                to: '/school-order-history',
              },
              {
                label: 'Subscription',
                description: 'Update your subscription payment info',
                icon: <CreditCard size={18} />,
                to: '/update-payment-info',
              },
            ],
          },
        ],
      },
    },
  ];
};

/**
 * School Teacher Menu Configuration with Mega Menu
 */
const getSchoolTeacherMenuConfig = () => [
  {
    label: 'Home',
    megaMenu: {
      sections: [
        {
          title: 'Overview',
          items: [
            {
              label: 'My Dashboard',
              description: 'View progress',
              icon: <Home size={18} />,
              to: '/main-dashboard',
            },
          ],
        },
        {
          title: 'Certifications',
          items: [
            {
              label: 'My Certifications',
              description: 'Track your certifications',
              icon: <Award size={18} />,
              to: '/my-certifications',
            },
          ],
        },
      ],
    },
  },
  {
    label: 'Account',
    megaMenu: {
      sections: [
        {
          title: 'Profile',
          items: [
            {
              label: 'Update Profile',
              description: 'Edit your profile information',
              icon: <User size={18} />,
              to: '/profile',
            },
            {
              label: 'Change Password',
              description: 'Update your password',
              icon: <Lock size={18} />,
              to: '/change-password',
            },
          ],
        },
      ],
    },
  },
];

/**
 * Teacher Menu Configuration with Mega Menu
 */
const getTeacherMenuConfig = () => [
  {
    label: 'Home',
    megaMenu: {
      sections: [
        {
          title: 'Overview',
          items: [
            {
              label: 'My Dashboard',
              description: 'View progress',
              icon: <Home size={18} />,
              to: '/main-dashboard',
            },
          ],
        },
        {
          title: 'Quick Actions',
          items: [
            {
              label: 'My Certifications',
              description: 'Track your certifications',
              icon: <Award size={18} />,
              to: '/my-certifications',
            },
          ],
        },
      ],
    },
  },
  {
    label: 'Account',
    megaMenu: {
      sections: [
        {
          title: 'Profile',
          items: [
            {
              label: 'Update Profile',
              description: 'Edit your profile information',
              icon: <User size={18} />,
              to: '/profile',
            },
            {
              label: 'Change Password',
              description: 'Update your password',
              icon: <Lock size={18} />,
              to: '/change-password',
            },
          ],
        },
        {
          title: 'Certifications',
          items: [
            {
              label: 'My Certifications',
              description: 'Track your certifications',
              icon: <Award size={18} />,
              to: '/my-certifications',
            },
          ],
        },
        {
          title: 'Billing',
          items: [
            {
              label: 'Order History',
              description: 'View past orders',
              icon: <ShoppingCart size={18} />,
              to: '/teacher-order-history',
            },
            {
              label: 'Payment',
              description: 'Manage payment',
              icon: <CreditCard size={18} />,
              to: '/payment',
            },
            {
              label: 'Subscription',
              description: 'Update your subscription payment info',
              icon: <CreditCard size={18} />,
              to: '/update-payment-info',
            },
          ],
        },
      ],
    },
  },
];

